/**
 * Game registry — one entry per supported game. Adding a new game means:
 *
 *   1. Add the kind to the `GameKind` union in `shared/protocol.ts`.
 *   2. Register the adapter on the server (`server/src/lobby/Lobby.ts`).
 *   3. Append a `GameRegistration` here with the game's React components.
 *
 * That's it — `GamePage` and `LobbyPage` dispatch through this registry
 * rather than holding hardcoded `gameKind === "xxx"` switches.
 */

import type { ComponentType } from "react";
import type {
  ClientMsg,
  GameEvent,
  GameKind,
  LobbyDetail,
  PrivateGameData,
  PublicGameState,
} from "../../../shared/protocol.js";
import { ShengJiGame, EventLog as ShengJiEventLog } from "./shengji/ShengJiGame.js";
import { HeartsGame } from "./hearts/HeartsGame.js";
import { CaboGame, CaboEventLog } from "./cabo/CaboGame.js";
import { OhHellGame } from "./ohhell/OhHellGame.js";
import { EuchreGame } from "./euchre/EuchreGame.js";
import { RegicideGame } from "./regicide/RegicideGame.js";
import { SwoopGame } from "./swoop/SwoopGame.js";
import { ShengJiLobbyOptions } from "./shengji/ShengJiLobbyOptions.js";
import { CaboLobbyOptions } from "./cabo/CaboLobbyOptions.js";

/** Props every game view receives from `GamePage`. */
export interface GameViewProps {
  state: PublicGameState;
  yourPrivate: PrivateGameData | null;
  yourSeat: number | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}

/** Props every per-game lobby options block receives from `LobbyPage`. */
export interface LobbyOptionsProps {
  lobby: LobbyDetail;
  isHost: boolean;
  send: (m: ClientMsg) => void;
}

/** Props for an optional per-game event-log renderer in the gear panel. */
export interface EventLogProps {
  events: GameEvent[];
  state: PublicGameState;
}

export interface SeatBounds {
  min: number;
  max: number;
  default: number;
}

export interface GameRegistration {
  kind: GameKind;
  label: string;
  /**
   * Short one-line teaser shown on game tiles (the home-page picker). Keep
   * it ~80 chars — it lives under the game name in a small font.
   */
  tagline: string;
  /**
   * Longer blurb shown after a tile is selected and in the lobby header.
   * Markdown is not parsed — plain text only, can span a few sentences.
   */
  blurb: string;
  /**
   * Hex color used for the game's badge + tile accent. Pick something with
   * enough contrast against the dark felt — saturated mid-tones work well.
   */
  accent: string;
  /**
   * Allowed seat-count range and the default for fresh lobbies. Must match
   * the server-side `factory.seatBounds` exactly; the lobby UI uses this
   * to populate the player-count selector. (Source of truth on the server.)
   */
  seatBounds: SeatBounds;
  /** Main in-game view rendered by `GamePage` for this game. */
  GameView: ComponentType<GameViewProps>;
  /** Game-specific lobby options block; omit if the game has no options. */
  LobbyOptions?: ComponentType<LobbyOptionsProps>;
  /** Custom event-log renderer; falls back to a plain list if omitted. */
  EventLog?: ComponentType<EventLogProps>;
}

// ---------------------------------------------------------------------------
// Per-game thin adapters that narrow the unioned props into the typed shape
// each game component expects. Keeping these here means the game components
// can stay focused on rendering and not deal with the wire-protocol union.
// ---------------------------------------------------------------------------

function ShengJiView(props: GameViewProps) {
  if (props.state.kind !== "shengji") return null;
  const priv = props.yourPrivate?.kind === "shengji" ? props.yourPrivate : null;
  return (
    <ShengJiGame
      state={props.state.data}
      yourHand={priv?.hand ?? []}
      yourSeat={props.yourSeat}
      yourFriendCalls={priv?.myFriendCalls ?? null}
      events={props.events}
      send={props.send}
      mobile={props.mobile}
    />
  );
}

function HeartsView(props: GameViewProps) {
  if (props.state.kind !== "hearts") return null;
  const priv = props.yourPrivate?.kind === "hearts" ? props.yourPrivate : null;
  return (
    <HeartsGame
      state={props.state.data}
      yourHand={priv?.hand ?? []}
      yourStaged={priv?.staged ?? []}
      yourSeat={props.yourSeat}
      events={props.events}
      send={props.send}
      mobile={props.mobile}
    />
  );
}

function CaboView(props: GameViewProps) {
  if (props.state.kind !== "cabo") return null;
  const priv =
    props.yourPrivate?.kind === "cabo"
      ? {
          drawnCard: props.yourPrivate.drawnCard,
          initialPeekCards: props.yourPrivate.initialPeekCards,
          abilityRevealed: props.yourPrivate.abilityRevealed,
        }
      : null;
  return (
    <CaboGame
      state={props.state.data}
      yourSeat={props.yourSeat}
      yourPrivate={priv}
      events={props.events}
      send={props.send}
      mobile={props.mobile}
    />
  );
}

function ShengJiEventLogAdapter({ events, state }: EventLogProps) {
  if (state.kind !== "shengji") return null;
  return <ShengJiEventLog events={events} state={state.data} />;
}

function CaboEventLogAdapter({ events }: EventLogProps) {
  return <CaboEventLog events={events} />;
}

function OhHellView(props: GameViewProps) {
  if (props.state.kind !== "ohhell") return null;
  const priv = props.yourPrivate?.kind === "ohhell" ? props.yourPrivate : null;
  return (
    <OhHellGame
      state={props.state.data}
      yourHand={priv?.hand ?? []}
      yourSeat={props.yourSeat}
      events={props.events}
      send={props.send}
      mobile={props.mobile}
    />
  );
}

function EuchreView(props: GameViewProps) {
  if (props.state.kind !== "euchre") return null;
  const priv = props.yourPrivate?.kind === "euchre" ? props.yourPrivate : null;
  return (
    <EuchreGame
      state={props.state.data}
      yourHand={priv?.hand ?? []}
      yourSeat={props.yourSeat}
      events={props.events}
      send={props.send}
      mobile={props.mobile}
    />
  );
}

function RegicideView(props: GameViewProps) {
  if (props.state.kind !== "regicide") return null;
  const priv = props.yourPrivate?.kind === "regicide" ? props.yourPrivate : null;
  return (
    <RegicideGame
      state={props.state.data}
      yourHand={priv?.hand ?? []}
      yourSeat={props.yourSeat}
      events={props.events}
      send={props.send}
      mobile={props.mobile}
    />
  );
}

function SwoopView(props: GameViewProps) {
  if (props.state.kind !== "swoop") return null;
  const priv = props.yourPrivate?.kind === "swoop" ? props.yourPrivate : null;
  return (
    <SwoopGame
      state={props.state.data}
      yourHand={priv?.hand ?? []}
      yourSeat={props.yourSeat}
      events={props.events}
      send={props.send}
      mobile={props.mobile}
    />
  );
}

// ---------------------------------------------------------------------------
// Registrations
// ---------------------------------------------------------------------------

export const GAMES: GameRegistration[] = [
  {
    kind: "shengji",
    label: "Sheng Ji (Tractor)",
    tagline: "Team trick-taking. Climb from 2 to A. 4-6 players.",
    blurb:
      "Sheng Ji (Tractor) is a team trick-taking game. Plays with 4-6 players; the host picks fixed teams or Find-the-Friend in the lobby.",
    accent: "#d4af37",
    seatBounds: { min: 4, max: 6, default: 4 },
    GameView: ShengJiView,
    LobbyOptions: ShengJiLobbyOptions,
    EventLog: ShengJiEventLogAdapter,
  },
  {
    kind: "hearts",
    label: "Hearts",
    tagline: "Avoid the heart cards. Or take 'em all. 2-8 players.",
    blurb:
      "Hearts is a trick-taking game where the lowest score wins. Default 4 players; the host can adjust between 2 and 8 in the lobby. The 2 of clubs leads the first trick.",
    accent: "#e07a7a",
    seatBounds: { min: 2, max: 8, default: 4 },
    GameView: HeartsView,
  },
  {
    kind: "cabo",
    label: "Cabo",
    tagline: "Card-memory race. Keep your total low. 2-8 players.",
    blurb:
      "Cabo is a card-memory game for 2-8 players. Peek at your bottom two cards, then keep your total low - any card on the table can be snap-discarded at any time.",
    accent: "#5ec8e2",
    seatBounds: { min: 2, max: 8, default: 4 },
    GameView: CaboView,
    LobbyOptions: CaboLobbyOptions,
    EventLog: CaboEventLogAdapter,
  },
  {
    kind: "ohhell",
    label: "Oh Hell",
    tagline: "Bid exact tricks. Hands shrink, then grow. 3-7 players.",
    blurb:
      "Oh Hell is a bidding-and-tricks game for 3-7 players. Bid the exact number of tricks you'll take for a 10-point bonus; hand size descends from 10 to 1, then back up.",
    accent: "#a18bff",
    seatBounds: { min: 3, max: 7, default: 4 },
    GameView: OhHellView,
  },
  {
    kind: "euchre",
    label: "Euchre",
    tagline: "Bowers, bidding, partnership tricks. 4 players.",
    blurb:
      "Euchre is a 4-player partnership trick game with a 24-card deck (9-A). Jacks are bowers - the J of trump is the right bower, the J of the same color is the left bower (also trump). First team to 10 wins.",
    accent: "#6abf69",
    seatBounds: { min: 4, max: 4, default: 4 },
    GameView: EuchreView,
  },
  {
    kind: "regicide",
    label: "Regicide",
    tagline: "Trump and order can flip mid-round. 2-4 players.",
    blurb:
      "Regicide is a 2-4 player trick game where the trump suit and rank order can flip mid-round. The 10s, Jacks and Queens are removed before dealing; 9 cards each, race to 50. Best with 2.",
    accent: "#c84a96",
    seatBounds: { min: 2, max: 4, default: 2 },
    GameView: RegicideView,
  },
  {
    kind: "swoop",
    label: "Swoop",
    tagline: "Shed cards onto a falling pile. 3-8 players, 2-4 decks.",
    blurb:
      "Swoop is a 3-8 player shedding game played with 2-4 decks. Each player gets 19 cards (11 in hand, 4 face-up, 4 face-down mystery). Shed onto a central pile, but only play cards equal to or lower than the top; 10s and Jokers clear the pile. Lowest cumulative score at 500 wins.",
    accent: "#f08b5b",
    seatBounds: { min: 3, max: 8, default: 4 },
    GameView: SwoopView,
  },
];

export function gameByKind(kind: GameKind): GameRegistration | undefined {
  return GAMES.find((g) => g.kind === kind);
}
